import { UserS3UploadSignature } from '~/user/types';
import { Uploader, UploaderEvents } from '../Uploader';
import { UploaderRestrictions } from '../../values/UploaderRestrictions';
import { createUppy } from '../../uppy/createUppy';
import { addUploaderTracking } from './addUploaderTracking';

/**
 * This is a private map of all the uploader instances.
 */
const _uploadersMap = new Map<string, Uploader>();

export function getUploader({ uploaderId }: { uploaderId: string }): Uploader {
  const uploader = _uploadersMap.get(uploaderId);

  if (!uploader) {
    throw new Error(`No uploader found for ${uploaderId}`);
  }

  return uploader;
}

export function resetUploader({ uploaderId }: { uploaderId: string }): void {
  _uploadersMap.get(uploaderId)?.reset();
}

export function destroyUploader({ uploaderId }: { uploaderId: string }): void {
  _uploadersMap.get(uploaderId)?.close(() => _uploadersMap.delete(uploaderId));
}

export function getOrCreateUploader({
  uploaderId,
  userId,
  s3UploadSignature,
  fileRestrictions = UploaderRestrictions.new(),
  eventHandlers = {},
}: {
  uploaderId: string;
  userId: string;
  s3UploadSignature: UserS3UploadSignature;
  fileRestrictions?: UploaderRestrictions;
  eventHandlers?: Partial<UploaderEvents>;
}): Uploader {
  const existing = _uploadersMap.get(uploaderId);
  if (existing) {
    return existing;
  }

  const uppyInstance = createUppy({
    id: uploaderId,
    userId,
    s3UploadSignature,
    fileRestrictions,
  });

  const uploaderInstance = new Uploader(
    uploaderId,
    userId,
    s3UploadSignature,
    uppyInstance,
    fileRestrictions
  );

  Object.keys(eventHandlers).forEach((eventName) => {
    const handler = eventHandlers[eventName];
    if (handler) {
      uploaderInstance.on(eventName as keyof UploaderEvents, handler);
    }
  });

  addUploaderTracking(uploaderInstance);

  _uploadersMap.set(uploaderId, uploaderInstance);

  return uploaderInstance;
}

export function destroyAllUploaders(): void {
  _uploadersMap.forEach((uploader) =>
    destroyUploader({ uploaderId: uploader.getId() })
  );
}
